import { action, computed, makeObservable, observable } from "mobx";
import { Subscription } from "zen-observable-ts";
import { SUBSCRIBE_CUSTOMER_COPY_JOBS_BY_ID } from "src/api/customer";
import {
    SubscribeCustomerCopyJobsById,
    SubscribeCustomerCopyJobsByIdVariables,
    SubscribeCustomerCopyJobsById_copy_jobs as SubscribedCopyJob
} from "src/api/generated/SubscribeCustomerCopyJobsById";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { authorizedFetch } from "src/network/authorizedFetch";
import { NetworkConfig } from "src/network/NetworkConfig";
import { ICustomerCopyJob } from "src/stores/CustomerDetailPageStore";
import { RootStore } from "src/stores/RootStore";

const RUNNING_COPY_JOB_STATUS = 1;
const FAILED_COPY_JOB_STATUS = 3;

interface ICustomerLatestCopyJobResponse {
    /** null, wenn der Kunde noch nie kopiert wurde. */
    latestCopyJob: ICustomerCopyJob | null;
}

/**
 * Die Subscription liefert die Spaltennamen aus Hasura, der Endpoint die des Kundenportals.
 * Im Store liegt nur eine Form, damit die Anzeige nicht wissen muss, woher ein Auftrag stammt.
 */
const toCopyJob = (subscribedCopyJob: SubscribedCopyJob): ICustomerCopyJob => ({
    status: subscribedCopyJob.status,
    progressPercent: subscribedCopyJob.progress_percent,
    errorMessage: subscribedCopyJob.error_message,
    createdAt: subscribedCopyJob.created_at
});

export class CustomerCopyJobsStore {
    rootStore: RootStore;

    @observable activeCustomerId: string | null = null;
    @observable.ref copyJobs: ICustomerCopyJob[] = [];

    private copyJobsSubscription: Subscription | undefined = undefined;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeObservable(this);
    }

    @computed
    get latestCopyJob() {
        return this.copyJobs[0];
    }

    @computed
    get latestCopyErrorMessage() {
        return this.latestCopyJob?.status === FAILED_COPY_JOB_STATUS && this.latestCopyJob.errorMessage
            ? this.latestCopyJob.errorMessage
            : null;
    }

    @action
    reset = () => {
        this.stopSubscriptionCustomerCopyJobs();
        this.activeCustomerId = null;
        this.copyJobs = [];
    };

    /**
     * Uebernimmt die Kopierauftraege, die mit der Detailseite geladen wurden, und abonniert den
     * Fortschritt, solange ein Auftrag laeuft.
     */
    @action
    initializeCopyJobs = (customerid: string, copyJobs: ICustomerCopyJob[]) => {
        this.stopSubscriptionCustomerCopyJobs();

        this.activeCustomerId = customerid;
        this.copyJobs = copyJobs;

        if (this.shouldSubscribeToCopyJob(copyJobs[0]?.status)) {
            this.startSubscriptionCustomerCopyJobs(customerid);
        }
    };

    /**
     * Fragt nur den neuesten Kopierauftrag ab, etwa bevor der Kunde gelöscht wird. Wirft, wenn die
     * Abfrage scheitert — ein unbekannter Status darf nicht als "es wird nicht kopiert" gelten.
     */
    fetchLatestCopyJob = async (customerId: string) => {
        const { latestCopyJob } = await authorizedFetch<ICustomerLatestCopyJobResponse>(
            this.rootStore.authStore,
            NetworkConfig.customerLatestCopyJobUrl(customerId),
            "customer_detail_page"
        );

        return latestCopyJob;
    };

    startSubscriptionCustomerCopyJobs(customerid: string) {
        this.stopSubscriptionCustomerCopyJobs();

        if (!customerid) {
            return;
        }

        const copyJobsSubscription = apolloClientInstance
            .subscribe<SubscribeCustomerCopyJobsById, SubscribeCustomerCopyJobsByIdVariables>({
                query: SUBSCRIBE_CUSTOMER_COPY_JOBS_BY_ID,
                variables: { customerid }
            })
            .subscribe({
                next: ({ data }) => {
                    if (this.activeCustomerId !== customerid) {
                        return;
                    }

                    const copyJobs = (data?.copy_jobs ?? []).map(toCopyJob);
                    this.setCopyJobs(copyJobs);

                    if (!this.shouldSubscribeToCopyJob(copyJobs[0]?.status)) {
                        this.stopSubscriptionCustomerCopyJobs();
                    }
                },
                error: () => {
                    // Error is logged by Apollo errorLink.
                    this.stopSubscriptionCustomerCopyJobs();
                }
            });

        this.setCopyJobsSubscription(copyJobsSubscription);
    }

    stopSubscriptionCustomerCopyJobs() {
        if (this.copyJobsSubscription) {
            this.copyJobsSubscription.unsubscribe();
            this.setCopyJobsSubscription(undefined);
        }
    }

    @action
    setCopyJobs = (copyJobs: ICustomerCopyJob[]) => {
        this.copyJobs = copyJobs;
    };

    @action
    setCopyJobsSubscription = (copyJobsSubscription: Subscription | undefined) => {
        this.copyJobsSubscription = copyJobsSubscription;
    };

    private shouldSubscribeToCopyJob(status: number | null | undefined) {
        return status === RUNNING_COPY_JOB_STATUS;
    }
}
